import { RequireAuthAction } from "@/components/auth/RequireAuthAction";
import { DaoRulesDialog } from "@/components/governance/DaoRulesDialog";
import { MmkStakeWidget } from "@/components/governance/MmkStakeWidget";
import { MmkSwapWidget } from "@/components/governance/MmkSwapWidget";
import { Button } from "@/components/ui/Button";
import { Pagination } from "@/components/ui/Pagination";
import {
	useCreateProposal,
	useExecuteProposal,
	useStakingBalance,
	useVoteProposal,
} from "@/hooks/useGovernance";
import { useGetPublicGovernanceProposals } from "@/hooks/usePublicProtocol";
import { useAuth } from "@/lib/auth";
import { docsArticlePath } from "@/lib/docs/parse";
import { ROUTES } from "@/lib/routes";
import { cn, formatNum } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import { useMemo, useState } from "react";
import { BrandTokenMark } from "@/components/brand/BrandTokenMark";
import { Link } from "react-router-dom";

type Filter = "active" | "passed" | "rejected" | "executed" | "all";
const GOV_PAGE = 8;

export default function GovernancePage() {
	const [filter, setFilter] = useState<Filter>("active");
	const [openId, setOpenId] = useState<number | null>(null);
	const [offset, setOffset] = useState(0);
	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [rulesOpen, setRulesOpen] = useState(false);
	const [daoSafetyOpen, setDaoSafetyOpen] = useState(false);
	const { isAuthenticated } = useAuth();
	const { data: staked = 0 } = useStakingBalance();
	const { data, isLoading } = useGetPublicGovernanceProposals();
	const vote = useVoteProposal();
	const create = useCreateProposal();
	const execute = useExecuteProposal();
	const proposals = data?.proposals ?? [];
	const activeCount = useMemo(
		() => proposals.filter((p) => p.status === "active").length,
		[proposals],
	);

	const filtered = useMemo(() => {
		const list =
			filter === "all"
				? proposals
				: proposals.filter(
						(p) => p.status === filter || (filter === "active" && !p.status),
					);
		return [...list].sort((a, b) => {
			const dc = (b.created ?? 0) - (a.created ?? 0);
			if (dc !== 0) return dc;
			return Number(b.id) - Number(a.id);
		});
	}, [filter, proposals]);

	const rows = filtered.slice(offset, offset + GOV_PAGE);
	const total = filtered.length;

	function formatCreatedNs(ns: number | undefined): string {
		if (!ns || !Number.isFinite(ns)) return "";
		const ms = ns > 1e15 ? ns / 1e6 : ns;
		try {
			return new Date(ms).toLocaleDateString(undefined, {
				year: "numeric",
				month: "short",
				day: "numeric",
			});
		} catch {
			return "";
		}
	}

	return (
		<div className="container mx-auto max-w-3xl space-y-10 px-4 py-6 sm:py-8 pb-16">
			<div
				id="section-gov-header"
				className="flex scroll-mt-24 flex-wrap items-start justify-between gap-3"
			>
				<div className="flex items-start gap-3">
					<div className="flex items-center gap-1.5 pt-1">
						<BrandTokenMark token="MMK" size="lg" />
					</div>
					<div>
					<p className="section-eyebrow">Governance · $MMK</p>
					<h1 className="font-display text-xl font-bold tracking-tight sm:text-2xl">
						MotokoMaker
					</h1>
					<p className="mt-1 text-sm text-muted-foreground">
						Buy MMK, stake for vote weight, and govern parameters within hard
						bounds. Wasm upgrades stay with canister controllers — not the DAO.{" "}
						<Link to={ROUTES.savings} className="font-semibold text-primary">
							USDM savings →
						</Link>
					</p>
					<p className="mt-1 text-xs">
						<Link
							to={docsArticlePath("governance", "overview")}
							className="font-semibold text-primary underline"
						>
							Docs · how it works
						</Link>
					</p>
					</div>
				</div>
				{isAuthenticated ? (
					<span className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs">
						Vote weight{" "}
						<span className="font-mono font-bold text-primary">
							{formatNum(staked, 2)} staked MMK
						</span>
					</span>
				) : null}
			</div>

			<section className="card-surface border-primary/20 text-sm">
				<button
					type="button"
					className="flex w-full flex-wrap items-center justify-between gap-2 p-5 text-left"
					aria-expanded={daoSafetyOpen}
					onClick={() => setDaoSafetyOpen((o) => !o)}
				>
					<p className="text-xs font-bold uppercase tracking-wide text-primary">
						DAO safety
					</p>
					<span className="inline-flex items-center gap-2 text-[11px] text-muted-foreground">
						{daoSafetyOpen ? "Hide" : "Show"}
						<ChevronDown
							className={cn(
								"size-4 transition-transform",
								daoSafetyOpen && "rotate-180",
							)}
						/>
					</span>
				</button>
				{daoSafetyOpen ? (
					<div className="space-y-2 border-t border-border/60 px-5 pb-5 pt-3">
						<div className="flex flex-wrap justify-end">
							<button
								type="button"
								className="rounded-full border border-primary/40 px-3 py-1 text-[11px] font-bold text-primary"
								onClick={() => setRulesOpen(true)}
							>
								10 key DAO rules
							</button>
						</div>
						<ul className="list-inside list-disc space-y-1 text-muted-foreground">
							<li>
								<strong className="text-foreground">Can:</strong> tune fees, CR /
								liq threshold, ceilings, pauses, surplus waterfall — within
								validated ranges.
							</li>
							<li>
								<strong className="text-foreground">Cannot:</strong> upgrade wasm,
								change controllers, or mint MMK to cover vault bad debt.
							</li>
							<li>
								Param proposals need staked MMK quorum; execute after vote +
								timelock. New proposals start{" "}
								<strong className="text-foreground">active</strong> for voting —
								they are not auto-passed.
							</li>
							<li>
								<strong className="text-foreground">Vote vesting:</strong> new
								stake has a <strong className="text-foreground">24h cliff</strong>{" "}
								then linear unlock over 180 days — stake-and-vote flash games are
								blocked.
							</li>
							<li>
								<strong className="text-foreground">Phishing:</strong> only vote
								and stake from this app on the official IC assets URL. Never
								approve MMK spending from DMs, fake “governance alerts,” or
								look-alike sites. Check the canister URL / custom domain before
								signing.
							</li>
						</ul>
						<p className="text-[11px] text-muted-foreground">
							Full catalog:{" "}
							<Link
								to={docsArticlePath("governance", "overview")}
								className="font-semibold text-primary"
							>
								/docs/governance/overview
							</Link>
						</p>
					</div>
				) : null}
			</section>

			<DaoRulesDialog open={rulesOpen} onClose={() => setRulesOpen(false)} />

			<section id="section-get-mmk" className="scroll-mt-24 space-y-3">
				<div className="flex flex-wrap items-end justify-between gap-2">
					<div>
						<h2 className="text-lg font-bold">Get MMK</h2>
						<p className="text-sm text-muted-foreground">
							Only-up power-law USDM → MMK. Proceeds support protocol inventory.
						</p>
					</div>
					<Link
						to={docsArticlePath("governance", "only-up-swap")}
						className="text-xs font-semibold text-primary underline"
					>
						Docs · only-up swap
					</Link>
				</div>
				<MmkSwapWidget />
			</section>

			<section id="section-stake" className="scroll-mt-24 space-y-3">
				<h2 className="text-lg font-bold">Stake MMK</h2>
				<p className="text-sm text-muted-foreground">
					Stake for vote weight on proposals.
				</p>
				<MmkStakeWidget />
			</section>

			<section id="section-proposals" className="scroll-mt-24 space-y-6">
				<div>
					<h2 className="text-lg font-bold">Proposals</h2>
					<p className="text-sm text-muted-foreground">
						Create, vote, and execute — all visible without switching tabs.
					</p>
				</div>
				<div className="space-y-6">
					<div className="card-surface space-y-3 p-5">
						<p className="text-sm font-semibold">Create proposal</p>
						<p className="text-xs text-muted-foreground">
							Text proposals (requires staked MMK per protocol rules).
						</p>
						<input
							placeholder="Title"
							value={title}
							onChange={(e) => setTitle(e.target.value)}
							className="h-11 w-full rounded-xl border border-border bg-background px-3 text-sm outline-none focus:border-primary"
						/>
						<textarea
							placeholder="Description"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							rows={3}
							className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
						/>
						<RequireAuthAction
							labelWhenUnauthed="Connect to create"
							disabled={
								create.isPending || !title.trim() || !description.trim()
							}
							onAuthenticated={() =>
								create.mutate({
									title: title.trim(),
									description: description.trim(),
								})
							}
						>
							{create.isPending ? "Creating…" : "Create proposal"}
						</RequireAuthAction>
					</div>

					<div className="flex flex-wrap gap-1 rounded-full border border-border p-1 sm:w-fit">
						{(
							[
								["active", "Active"],
								["passed", "Passed"],
								["rejected", "Rejected"],
								["executed", "Executed"],
								["all", "All"],
							] as const
						).map(([id, label]) => (
							<button
								key={id}
								type="button"
								onClick={() => {
									setFilter(id);
									setOffset(0);
								}}
								className={cn(
									"inline-flex min-h-10 items-center gap-1.5 rounded-full px-4 text-xs font-semibold",
									filter === id
										? "bg-primary/15 text-primary"
										: "text-muted-foreground",
								)}
							>
								{label}
								{id === "active" && activeCount > 0 ? (
									<span
										className={cn(
											"inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold",
											filter === "active"
												? "bg-primary text-primary-foreground"
												: "bg-muted text-foreground",
										)}
									>
										{activeCount}
									</span>
								) : null}
							</button>
						))}
					</div>

					{isLoading ? (
						<div className="space-y-3">
							{Array.from({ length: 3 }).map((_, i) => (
								<div key={i} className="skeleton-shimmer h-24" />
							))}
						</div>
					) : rows.length === 0 ? (
						<div className="card-surface p-6 text-center text-sm text-muted-foreground">
							No proposals in this filter.
						</div>
					) : (
						<ul className="space-y-3">
							{rows.map((p) => {
									const id = Number(p.id);
									const open = openId === id;
									const createdLabel = formatCreatedNs(p.created);
									return (
										<li key={String(p.id)} className="card-surface overflow-hidden">
											<button
												type="button"
												className="flex w-full items-start justify-between gap-3 p-4 text-left"
												onClick={() => setOpenId(open ? null : id)}
											>
												<div>
													<p className="font-mono text-[10px] text-muted-foreground">
														#{String(p.id)}
														{createdLabel ? ` · ${createdLabel}` : ""}
													</p>
													<p className="mt-0.5 text-sm font-semibold">
														{p.title ?? "Proposal"}
													</p>
													<p className="mt-1 text-xs capitalize text-muted-foreground">
														{p.status ?? "active"} · for{" "}
														{formatNum(p.forVotes ?? 0, 0)} · against{" "}
														{formatNum(p.againstVotes ?? 0, 0)}
													</p>
												</div>
												<span className="text-xs text-primary">
													{open ? "Hide" : "Details"}
												</span>
											</button>
											{open ? (
												<div className="space-y-3 border-t border-border px-4 py-4">
													<p className="text-sm text-muted-foreground">
														{p.description ?? "On-chain proposal."}
													</p>
													<div className="flex flex-wrap gap-2">
														{(p.status ?? "active") === "active" ? (
															<>
																<RequireAuthAction
																	labelWhenUnauthed="Connect to vote"
																	disabled={vote.isPending}
																	onAuthenticated={() =>
																		vote.mutate({ id, support: true })
																	}
																	className="flex-1"
																>
																	Vote for
																</RequireAuthAction>
																<Button
																	variant="secondary"
																	className="flex-1"
																	disabled={vote.isPending || !isAuthenticated}
																	onClick={() =>
																		vote.mutate({ id, support: false })
																	}
																>
																	Vote against
																</Button>
															</>
														) : null}
														{(p.status ?? "") === "passed" ? (
															<RequireAuthAction
																labelWhenUnauthed="Connect to execute"
																disabled={execute.isPending}
																onAuthenticated={() => execute.mutate(id)}
																className="flex-1"
															>
																{execute.isPending
																	? "Executing…"
																	: "Execute proposal"}
															</RequireAuthAction>
														) : null}
													</div>
												</div>
											) : null}
										</li>
									);
								})}
						</ul>
					)}
					{total > GOV_PAGE ? (
						<Pagination
							offset={offset}
							pageSize={GOV_PAGE}
							total={total}
							onOffsetChange={setOffset}
						/>
					) : null}
				</div>
			</section>
		</div>
	);
}
